<script setup lang="ts">
import {
  addProtocol,
  GPUInitializationError,
  Map,
  type MapOptions,
  removeProtocol,
  setWorkerUrl,
} from "maplibre-gl";
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import { Protocol } from "pmtiles";
import { onMounted, onUnmounted, ref, useTemplateRef } from "vue";

export type BaseMapOptions = Omit<MapOptions, "container">;

const { initialOptions } = defineProps<{
  initialOptions?: BaseMapOptions;
}>();

const emit = defineEmits<{
  loaded: [map: Map];
}>();

setWorkerUrl(workerUrl);

const $map = useTemplateRef("$map");
const mapError = ref<string>();

onMounted(() => {
  if (!$map.value) {
    return;
  }

  const protocol = new Protocol();

  addProtocol("pmtiles", protocol.tile);

  try {
    const map = new Map({
      ...initialOptions,
      container: $map.value,
    });

    map.on("load", () => emit("loaded", map));
  } catch (error) {
    mapError.value =
      error instanceof GPUInitializationError
        ? "Kortet kan ikke vises, fordi browseren ikke kunne starte grafikvisningen. Prøv en anden browser."
        : "Kortet kunne ikke indlæses. Prøv at genindlæse siden.";
  }
});

onUnmounted(() => {
  removeProtocol("pmtiles");
});
</script>

<template>
  <div>
    <div
      v-if="mapError"
      class="h-100 d-flex align-items-center justify-content-center p-3"
    >
      <div class="alert alert-warning mb-0" role="alert">
        {{ mapError }}
      </div>
    </div>
    <div v-else ref="$map" class="h-100"></div>
  </div>
</template>

<style lang="scss">
@import "maplibre-gl/dist/maplibre-gl.css";
</style>
